import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    getDashboardData,
    createOrUpdateAssistant,
    uploadBackgroundImage,
    updateAssistantBackgroundImage,
    createPersonalContextEntry,
    createPersonalContextEntriesFromCategories,
    updatePersonalContextEntry,
    clearContextCardsCache,
    supabase
} from '../services/supabase';
import { DashboardData, AssistantFormData, PersonalContextEntry } from '../types';
import { generateUniqueUrlWithCheck, generateUniqueUrl, sanitizeUrlHandle, checkUrlHandleUniqueness } from '../utils/helpers';
import { handleSupabaseError } from '../utils/errorHandler';
import { DEFAULT_COLORS, VALIDATION, ROUTES, MESSAGES } from '../constants';
import { forceRefreshQuestionAnalysis, getNewQuestionsCount } from '../services/supabase';
import {
    DashboardNavigation,
    YourEchooPage,
    BuildYourStoryPage,
    InsightsPage,
    OverviewPage,
    PersonalContextPage,
    ProfileSettingsPage
} from '../components/dashboard';
import { DashboardPage } from '../types/dashboard';
import {
    getCharacterCountColor,
    validateField,
    isFieldValid
} from '../utils/dashboardUtils';
import {
    Plus,
    Settings,
    LogOut,
    Users,
    Sparkles,
} from 'lucide-react';

const Dashboard: React.FC = () => {
    const { user, signOut } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState<DashboardPage>('your-echoo');
    const [editingField, setEditingField] = useState<string | null>(null);
    const [editLoading, setEditLoading] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [originalColor, setOriginalColor] = useState<string>('');
    const [tempName, setTempName] = useState('');
    const [tempPronouns, setTempPronouns] = useState('');
    const [formData, setFormData] = useState<AssistantFormData>({
        name: '',
        pronouns: '',
        primary_color: DEFAULT_COLORS.PRIMARY,
        secondary_color: DEFAULT_COLORS.SECONDARY,
        unique_url: '',
    });
    const [personalContextEntries, setPersonalContextEntries] = useState<PersonalContextEntry[]>([]);
    const [tempValue, setTempValue] = useState('');
    const [newQuestionsCount, setNewQuestionsCount] = useState<number>(0);
    const [imageUploadLoading, setImageUploadLoading] = useState(false);
    const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | undefined>(undefined);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingContext, setEditingContext] = useState<string>('');
    const [editingTitle, setEditingTitle] = useState<string>('');
    const [currentPrompt, setCurrentPrompt] = useState<string>('');
    const [isAddingNew, setIsAddingNew] = useState(false);

    const handleRefreshAnalysis = async (): Promise<void> => {
        if (!dashboardData?.assistant) return;

        try {
            const newAnalysis = await forceRefreshQuestionAnalysis(
                dashboardData.assistant.id,
                dashboardData.assistant.name
            );

            if (newAnalysis) {
                setDashboardData(prev => prev ? {
                    ...prev,
                    questionAnalysis: newAnalysis
                } : null);
                setNewQuestionsCount(0);
            }
        } catch (error) {
            console.error('Error refreshing analysis:', error);
        }
    };

    const fetchDashboardData = React.useCallback(async () => {
        if (!user) return;

        try {
            const data = await getDashboardData(user.id);
            setDashboardData(data);

            if (data.assistant) {
                const assistantFormData = {
                    name: data.assistant.name,
                    pronouns: data.assistant.pronouns || '',
                    primary_color: data.assistant.primary_color,
                    secondary_color: data.assistant.secondary_color,
                    unique_url: data.assistant.unique_url,
                };
                setFormData(assistantFormData);
                setPersonalContextEntries(data.assistant.personal_context_entries || []);
                setBackgroundImageUrl(data.assistant.background_image_url);

                if (data.sampleQuestions && data.sampleQuestions.length > 0) {
                    const newCount = await getNewQuestionsCount(data.assistant.id, data.sampleQuestions);
                    setNewQuestionsCount(newCount);
                }
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    const checkAndCreateGeneratedCategories = React.useCallback(async () => {
        if (!user || !dashboardData?.assistant) return;

        try {
            // Check if there are generated categories in localStorage
            const generatedCategoriesStr = localStorage.getItem('generatedCategories');
            if (generatedCategoriesStr) {
                const generatedCategories = JSON.parse(generatedCategoriesStr);

                // Check if we already have personal context entries
                if (personalContextEntries.length === 0) {
                    console.log('Creating personal context entries from generated categories:', generatedCategories);

                    // Create personal context entries from the generated categories
                    const newEntries = await createPersonalContextEntriesFromCategories(
                        dashboardData.assistant.id,
                        generatedCategories
                    );

                    if (newEntries.length > 0) {
                        setPersonalContextEntries(newEntries);
                        console.log('Created', newEntries.length, 'personal context entries');

                        // Clear the localStorage after creating entries
                        localStorage.removeItem('generatedCategories');
                        localStorage.removeItem('learningObjective');
                    }
                }
            }
        } catch (error) {
            console.error('Error creating personal context entries from categories:', error);
        }
    }, [user, dashboardData?.assistant, personalContextEntries.length]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    useEffect(() => {
        checkAndCreateGeneratedCategories();
    }, [checkAndCreateGeneratedCategories]);

    // Handle URL parameter for initial page
    useEffect(() => {
        const pageParam = searchParams.get('page');
        if (pageParam && ['your-echoo', 'build-story', 'insights'].includes(pageParam)) {
            setCurrentPage(pageParam as DashboardPage);
        }
    }, [searchParams]);

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleImageUpload = async (file: File): Promise<void> => {
        if (!user) return;

        setImageUploadLoading(true);
        try {
            const uploadedUrl = await uploadBackgroundImage(file, user.id);
            if (uploadedUrl) {
                setBackgroundImageUrl(uploadedUrl);

                if (dashboardData?.assistant) {
                    await updateAssistantBackgroundImage(user.id, uploadedUrl);
                }
            } else {
                alert('Failed to upload image. Please try again.');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image. Please try again.');
        } finally {
            setImageUploadLoading(false);
        }
    };

    const handleImageRemove = async (): Promise<void> => {
        if (!user) return;

        setImageUploadLoading(true);
        try {
            setBackgroundImageUrl(undefined);

            if (dashboardData?.assistant) {
                await updateAssistantBackgroundImage(user.id, '');
            }
        } catch (error) {
            console.error('Error removing image:', error);
            alert('Failed to remove image. Please try again.');
        } finally {
            setImageUploadLoading(false);
        }
    };

    const handleSaveContext = async () => {
        if (!editingTitle.trim()) return;

        if (isAddingNew) {
            // Create new personal context entry
            if (user && dashboardData?.assistant) {
                const newEntry = await createPersonalContextEntry(dashboardData.assistant.id, {
                    title: editingTitle,
                    content: tempValue,
                    display_order: personalContextEntries.length + 1,
                });

                if (newEntry) {
                    setPersonalContextEntries(prev => [...prev, newEntry]);
                    // Regenerate context cards cache
                    await clearContextCardsCache(dashboardData.assistant.id);
                }
            }
        } else {
            // Update existing personal context entry
            if (editingContext) {
                // Find the existing entry to preserve description and prompt
                const existingEntry = personalContextEntries.find(entry => entry.id === editingContext);

                const updatedEntry = await updatePersonalContextEntry(editingContext, {
                    title: editingTitle,
                    content: tempValue,
                    description: existingEntry?.description, // Preserve existing description
                    prompt: existingEntry?.prompt, // Preserve existing prompt
                });

                if (updatedEntry) {
                    console.log('Updated entry:', updatedEntry);
                    console.log('Existing entry before update:', existingEntry);

                    setPersonalContextEntries(prev =>
                        prev.map(entry =>
                            entry.id === editingContext ? updatedEntry : entry
                        )
                    );
                    // Regenerate context cards cache
                    if (dashboardData?.assistant) {
                        await clearContextCardsCache(dashboardData.assistant.id);
                    }
                }
            }
        }

        setShowEditModal(false);
        setIsAddingNew(false);
        setEditingContext('');
        setEditingTitle('');
    };

    const startEditing = (field: string, value: string) => {
        setEditingField(field);
        setTempValue(value);
    };

    const startEditingNameAndPronouns = () => {
        setEditingField('name_and_pronouns');
        setTempName(assistant!.name);
        setTempPronouns(assistant!.pronouns || '');
    };

    const cancelEditing = () => {
        setEditingField(null);
        setTempValue('');
        setTempName('');
        setTempPronouns('');
    };

    const saveField = async (field: string) => {
        // This function is no longer needed for personal context fields
        // Personal context entries are now handled separately
        console.warn('saveField is deprecated for personal context fields');
    };

    const saveNameAndPronouns = async () => {
        if (!user || !dashboardData?.assistant) return;

        const nameValidationError = validateField('name', tempName);
        if (nameValidationError) {
            alert(nameValidationError);
            return;
        }

        const pronounsValidationError = validateField('pronouns', tempPronouns);
        if (pronounsValidationError) {
            alert(pronounsValidationError);
            return;
        }

        setEditLoading(true);
        try {
            const updatedFormData = { ...formData, name: tempName, pronouns: tempPronouns };
            const assistantData = {
                name: updatedFormData.name,
                pronouns: updatedFormData.pronouns,
                primary_color: updatedFormData.primary_color,
                secondary_color: updatedFormData.secondary_color,
                unique_url: updatedFormData.unique_url,
            };

            const result = await createOrUpdateAssistant(user.id, assistantData, true, false);

            if (!result) {
                throw new Error('Failed to update assistant');
            }

            setFormData(updatedFormData);
            await fetchDashboardData();
            setEditingField(null);
            setTempName('');
            setTempPronouns('');
        } catch (error) {
            console.error('Error updating assistant:', error);
            if (error instanceof Error) {
                const errorMessage = handleSupabaseError(error, 'Dashboard');
                alert(errorMessage);
            } else {
                alert('Failed to update assistant. Please try again.');
            }
        } finally {
            setEditLoading(false);
        }
    };

    const saveColor = async (color: string) => {
        if (!user || !dashboardData?.assistant) return;

        setEditLoading(true);
        try {
            const updatedFormData = { ...formData, primary_color: color };
            const assistantData = {
                name: updatedFormData.name,
                pronouns: updatedFormData.pronouns,
                primary_color: updatedFormData.primary_color,
                secondary_color: updatedFormData.secondary_color,
                unique_url: updatedFormData.unique_url,
            };

            const result = await createOrUpdateAssistant(user.id, assistantData, true, false);

            if (!result) {
                throw new Error('Failed to update assistant');
            }

            setFormData(updatedFormData);
            await fetchDashboardData();
            setShowColorPicker(false);
        } catch (error) {
            console.error('Error updating assistant color:', error);
            if (error instanceof Error) {
                const errorMessage = handleSupabaseError(error, 'Dashboard');
                alert(errorMessage);
            } else {
                alert('Failed to update assistant color. Please try again.');
            }
        } finally {
            setEditLoading(false);
        }
    };

    const saveBasicInfo = async () => {
        if (!user || !dashboardData?.assistant) return;

        const nameValidationError = validateField('name', formData.name);
        if (nameValidationError) {
            alert(nameValidationError);
            return;
        }

        const pronounsValidationError = validateField('pronouns', formData.pronouns);
        if (pronounsValidationError) {
            alert(pronounsValidationError);
            return;
        }

        // Validate and sanitize URL handle
        const urlHandleValidationError = validateField('unique_url', formData.unique_url);
        if (urlHandleValidationError) {
            alert(urlHandleValidationError);
            return;
        }

        // Sanitize the URL handle
        const sanitizedUrlHandle = sanitizeUrlHandle(formData.unique_url);

        // Check if URL handle has changed
        if (sanitizedUrlHandle !== dashboardData.assistant.unique_url) {
            // Check uniqueness
            const isUnique = await checkUrlHandleUniqueness(sanitizedUrlHandle, supabase, user.id);
            if (!isUnique) {
                alert('This URL handle is already taken. Please choose a different one.');
                return;
            }
        }

        setEditLoading(true);
        try {
            const assistantData = {
                name: formData.name,
                pronouns: formData.pronouns,
                primary_color: formData.primary_color,
                secondary_color: formData.secondary_color,
                unique_url: sanitizedUrlHandle,
            };

            const result = await createOrUpdateAssistant(user.id, assistantData, true, false);

            if (!result) {
                throw new Error('Failed to update assistant');
            }

            await fetchDashboardData();
            alert('Basic information saved successfully!');
        } catch (error) {
            console.error('Error updating assistant:', error);
            if (error instanceof Error) {
                const errorMessage = handleSupabaseError(error, 'Dashboard');
                alert(errorMessage);
            } else {
                alert('Failed to update assistant. Please try again.');
            }
        } finally {
            setEditLoading(false);
        }
    };

    // Personal Context handlers
    const handleAddNew = () => {
        setIsAddingNew(true);
        setEditingTitle('');
        setTempValue('');
        setCurrentPrompt('');
        setShowEditModal(true);
    };

    const handleEditContext = (contextType: string, title: string, content: string) => {
        setIsAddingNew(false);
        setEditingContext(contextType);
        setEditingTitle(title);
        setTempValue(content);

        // Find the entry to get its prompt
        const entry = personalContextEntries.find(entry => entry.id === contextType);
        setCurrentPrompt(entry?.prompt || '');

        setShowEditModal(true);
    };

    const handleCancelModal = () => {
        setShowEditModal(false);
        setIsAddingNew(false);
        setEditingContext('');
        setEditingTitle('');
        setCurrentPrompt('');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600 font-medium">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading Dashboard</h2>
                    <p className="text-slate-600">Please wait while we prepare your personalized dashboard...</p>
                </div>
            </div>
        );
    }

    const { assistant, stats, hasAssistant } = dashboardData;

    return (
        <div className="min-h-screen bg-slate-100">
            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-8">
                {!hasAssistant ? (
                    // No Assistant State
                    <div className="text-center py-16">
                        <div className="max-w-md mx-auto">
                            <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <Users className="h-10 w-10 text-blue-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-3">
                                {MESSAGES.NO_ASSISTANT.title}
                            </h2>
                            <p className="text-slate-600 mb-8">
                                {MESSAGES.NO_ASSISTANT.description}
                            </p>
                            <Link
                                to={ROUTES.CREATE_ASSISTANT}
                                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl transform hover:-translate-y-0.5 transition-all duration-200"
                            >
                                <Plus className="w-5 h-5 mr-2" />
                                {MESSAGES.NO_ASSISTANT.action}
                            </Link>
                        </div>
                    </div>
                ) : (
                    // Has Assistant State - New Layout with Navigation
                    <div className="flex gap-6 min-h-screen">
                        {/* Left Navigation */}
                        <DashboardNavigation
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            userEmail={user?.email}
                            onSignOut={signOut}
                            primaryColor={assistant?.primary_color}
                            hasIncompleteSetup={personalContextEntries.length === 0 || !backgroundImageUrl}
                        />

                        {/* Main Content Area */}
                        <div className="flex-1 min-w-0 max-w-full py-8">
                            {currentPage === 'your-echoo' && assistant && (
                                <YourEchooPage
                                    assistant={assistant}
                                    stats={stats}
                                    personalContextEntries={personalContextEntries}
                                    backgroundImageUrl={backgroundImageUrl}
                                    onNavigateToBuildStory={() => setCurrentPage('build-story')}
                                />
                            )}
                            {currentPage === 'build-story' && (
                                <BuildYourStoryPage
                                    formData={formData}
                                    personalContextEntries={personalContextEntries}
                                    backgroundImageUrl={backgroundImageUrl}
                                    imageUploadLoading={imageUploadLoading}
                                    editLoading={editLoading}
                                    showEditModal={showEditModal}
                                    isAddingNew={isAddingNew}
                                    editingTitle={editingTitle}
                                    tempValue={tempValue}
                                    currentPrompt={currentPrompt}
                                    onFormDataChange={(data) => setFormData(prev => ({ ...prev, ...data }))}
                                    onSaveBasicInfo={saveBasicInfo}
                                    onSaveColor={saveColor}
                                    onImageUpload={handleImageUpload}
                                    onImageRemove={handleImageRemove}
                                    onAddNew={handleAddNew}
                                    onEditContext={handleEditContext}
                                    onSaveContext={handleSaveContext}
                                    onCancelModal={handleCancelModal}
                                    onTitleChange={setEditingTitle}
                                    onContentChange={setTempValue}
                                    getCharacterCountColor={getCharacterCountColor}
                                    isFieldValid={isFieldValid}
                                />
                            )}
                            {currentPage === 'insights' && assistant && (
                                <InsightsPage
                                    stats={stats}
                                    sampleQuestions={dashboardData?.sampleQuestions || []}
                                    questionAnalysis={dashboardData?.questionAnalysis}
                                    assistantName={assistant.name}
                                    assistantId={assistant.id}
                                    newQuestionsCount={newQuestionsCount}
                                    onRefreshAnalysis={handleRefreshAnalysis}
                                />
                            )}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default Dashboard;
